import {Layers, WalkOfShameLayers} from "./WalkOfShameLayers";
import {WalkOfShameGameMode} from "../server/WalkOfShameGameMode";
import {WalkOfShame} from "../WalkOfShame";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineLite} from "gsap";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {MathHelper} from "../../core/utils/MathHelper";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {WalkOfShameSound} from "../sound/WalkOfShameSound";

interface ILogoDetail {
    [mode: string]: {
        landscapePosition: PIXI.Point;
        portraitPosition: PIXI.Point;
        landscapeTexture: PIXI.Texture;
        portraitTexture: PIXI.Texture;
    };
}

export class WalkOfShameLogo extends PIXI.Container {
    private _logo: PIXI.Sprite;
    private _logoMainAnimation: PIXI.spine.Spine;
    private _logoDetails: ILogoDetail[] = [];
    private _canPlayParachuteBG: boolean = false;
    private _canPlayParachute: boolean = false;
    private _parachute: PIXI.Sprite;
    private _parachuteBG: PIXI.Sprite;
    private _parachuteP1: PIXI.Sprite;
    private _parachuteP2: PIXI.Sprite;
    private _parachuteBgTl: TimelineLite | null = null;
    private _parachuteTl: TimelineLite | null = null;
    private _parachuteSoundCtr: number = 0;
    private _isFSMode: boolean = false;
    private _mainLogoTl: TimelineLite | null;

    constructor() {
        super();

        this._logoDetails.push({
                "NORMAL": {
                    landscapePosition: new PIXI.Point(360, 45),
                    portraitPosition: new PIXI.Point(360, 45),
                    landscapeTexture: WalkOfShame.assetLoader.getTextures("logoColors_maingame/logoPurple")[0],
                    portraitTexture: WalkOfShame.assetLoader.getTextures("")[0]
                }
            },
            {
                "RESPIN": {
                    landscapePosition: new PIXI.Point(360, 45),
                    portraitPosition: new PIXI.Point(360, 45),
                    landscapeTexture: WalkOfShame.assetLoader.getTextures("logoColors_maingame/logoBlue")[0],
                    portraitTexture: WalkOfShame.assetLoader.getTextures("")[0]
                }
            },
            {
                "FREESPIN": {
                    landscapePosition: new PIXI.Point(-85, 106),
                    portraitPosition: new PIXI.Point(600, -45),
                    landscapeTexture: WalkOfShame.assetLoader.getTextures("logoColors_FS/LandscapeLogoPurple")[0],
                    portraitTexture: WalkOfShame.assetLoader.getTextures("logoColors_FS/portraitLogoPurple")[0]
                }
            });

        WalkOfShameLayers.getLayer(Layers.LOGO)?.addChild(this);
        this.init();
    }

    public onModeChange(mode: string, calledFromInit: boolean): void {
        this._isFSMode = false;

        this._logo.visible = true;
        this._logo.scale.set(1);

        this._canPlayParachuteBG = false;
        this._canPlayParachute = false;

        this.killTl(this._mainLogoTl);

        if (mode === WalkOfShameGameMode.NORMAL) {
            this._logo.texture = this._logoDetails[0][mode].landscapeTexture;
            this._logo.position = this._logoDetails[0][mode].landscapePosition;

            // #139
            if (WalkOfShame.model.isRestore && WalkOfShame.model.parsedInitData.nextMode === WalkOfShameGameMode.RESPIN) {
                this.playLogoAnimForReSpin();
            } else {
                this._logoMainAnimation.skeleton.setSkinByName("Red");
                this._logoMainAnimation.visible = true;
                this._logoMainAnimation.updateTransform();
                this._mainLogoTl = new TimelineLite({
                    repeat: -1
                });
                this._mainLogoTl.add(() => {
                    this.playMainLogoAnimation("blink");
                }, MathHelper.randomNumberInRange(10, 15));
            }

            this.turnOffAllParachutes();

            this._parachuteSoundCtr = 0;
        } else if (mode === WalkOfShameGameMode.RESPIN) {
            this._logo.texture = this._logoDetails[1][mode].landscapeTexture;
            this._logo.position = this._logoDetails[1][mode].landscapePosition;

            // #139
            this.playLogoAnimForReSpin();
        } else if (mode === WalkOfShameGameMode.FREESPIN) {
            this._isFSMode = true;

            this.setFSTexture(mode);
            this._logoMainAnimation.visible = false;
            this._logoMainAnimation.state.clearTracks();
        } else {
            this._logo.visible = false;
            this._logoMainAnimation.visible = false;
            this._logoMainAnimation.state.clearTracks();

            if (!NolimitApplication.isLandscape && !calledFromInit) {
                this._canPlayParachuteBG = true;
                if (!this._parachuteBgTl || !this._parachuteBgTl.isActive()) {
                    this.playParachuteBG(0);
                }

                if (mode === WalkOfShameGameMode.SUPER_FREESPIN_RESPIN) {
                    this._canPlayParachute = true;
                    this._parachuteSoundCtr = 0;
                    this.playParachute(false);
                }
            }
        }
    }

    public onOrientationChanged(): void {
        if (this._isFSMode) {
            this.setFSTexture(WalkOfShameGameMode.FREESPIN);
        }

        if (!NolimitApplication.isLandscape && WalkOfShame.model.parsedGameData && WalkOfShame.model.parsedGameData.nextMode !== WalkOfShameGameMode.NORMAL) {
            if (WalkOfShame.model.parsedGameData.mode === WalkOfShameGameMode.SUPER_FREESPIN_RESPIN) {
                this._canPlayParachute = true;
                if (this._parachute && this._parachuteTl) {
                    if (this._parachuteTl.isActive()) {
                        this._parachute.visible = true;
                    } else {
                        this.playParachute((this._parachuteSoundCtr++ % 4 === 0));
                    }
                }
            }

            if (WalkOfShame.model.parsedGameData.mode.indexOf(WalkOfShameGameMode.SUPER_FREESPIN) > -1) {
                this._canPlayParachuteBG = true;
                if (this._parachuteBG && this._parachuteBgTl) {
                    if (this._parachuteBgTl.isActive()) {
                        this._parachuteBG.visible = true;
                    } else {
                        this.playParachuteBG(0);
                    }
                }
            }
        } else {
            this._canPlayParachuteBG = false;
            this._canPlayParachute = false;
            if (this._parachute) {
                this._parachute.visible = false;
            }
            if (this._parachuteBG) {
                this._parachuteBG.visible = false;
            }
        }
    }

    // #153
    public pauseParachutesOnBigWin(): void {
        if (this._parachute) {
            this._parachute.visible = false;
        }

        if (this._parachuteTl && this._parachuteTl.isActive()) {
            this._parachuteTl.pause();
        }
    }

    // #153
    public resumeParachutesOnBigWin(): void {
        if (this._canPlayParachute && !NolimitApplication.isLandscape) {
            if (this._parachuteTl && this._parachuteTl.paused()) {
                this._parachuteTl.resume();

                if (this._parachute) {
                    this._parachute.visible = true;
                }
            }
        }
    }

    // #153
    public turnOffAllParachutes(): void {
        this.killTl(this._parachuteBgTl);
        if (this._parachuteBG) {
            this._parachuteBG.visible = false;
        }
        this.killTl(this._parachuteTl);
        if (this._parachute) {
            this._parachute.visible = false;
        }

        this._canPlayParachuteBG = false;
        this._canPlayParachute = false;
    }

    private init(): void {
        this._logo = new PIXI.Sprite();
        this._logo.visible = false;
        this._logo.anchor.set(0.5);

        this._logoMainAnimation = new PIXI.spine.Spine(WalkOfShame.assetLoader.getSpineAsset("logo"));
        this._logoMainAnimation.visible = false;
        this._logoMainAnimation.position.set(345, 54);

        this._parachuteP1 = new PIXI.Sprite(WalkOfShame.assetLoader.getTextures("effects/parachutePradeep")[0]);
        this._parachuteP1.visible = false;
        this._parachuteP1.anchor.set(0.5);

        this._parachuteP2 = new PIXI.Sprite(WalkOfShame.assetLoader.getTextures("effects/parachuteShivangi")[0]);
        this._parachuteP2.visible = false;
        this._parachuteP2.anchor.set(0.5);

        this.addChild(this._logo, this._logoMainAnimation, this._parachuteP1, this._parachuteP2);
    }

    private setFSTexture(mode: string): void {
        if (this._logo) {
            this._logo.scale.set(0.4);
            if (NolimitApplication.isLandscape) {
                this._logo.texture = this._logoDetails[2][mode].landscapeTexture;
                this._logo.position = this._logoDetails[2][mode].landscapePosition;
            } else {
                this._logo.texture = this._logoDetails[2][mode].portraitTexture;
                this._logo.position = this._logoDetails[2][mode].portraitPosition;
            }

            this._logo.visible = this.canShowFSLogo();
        }
    }

    private canShowFSLogo(): boolean {
        if (NolimitApplication.isLandscape) {
            return (NolimitApplication.screenBounds.width > 970);
        } else {
            return true;
        }
    }

    private playParachuteBG(delay: number): void {
        if (!this._canPlayParachuteBG) {
            return;
        }

        this.killTl(this._parachuteBgTl);

        const randomIdx: number = ArrayHelper.getRandomValue([-1, 1]),
            tl: TimelineLite = new TimelineLite({
                delay: delay
            }),
            screenBounds: ScreenBounds = NolimitApplication.screenBounds,
            center: number = screenBounds.center,
            duration: number = MathHelper.randomNumberInRange(15, 20),
            fromY: number = MathHelper.randomNumberInRange(screenBounds.top, screenBounds.top + 50);
        let fromX: number = MathHelper.randomNumberInRange(screenBounds.left, center),
            toX: number = MathHelper.randomNumberInRange(screenBounds.right + 100, screenBounds.right + 200);

        if (randomIdx === -1) {
            fromX = MathHelper.randomNumberInRange(screenBounds.right, center);
            toX = MathHelper.randomNumberInRange(screenBounds.left - 100, screenBounds.left - 200);
        }

        this._parachuteBG = ArrayHelper.getRandomValue([this._parachuteP1, this._parachuteP2]);
        this._parachuteBG.scale.set(0.1 * randomIdx, 0.1);

        tl.add(() => {
            this._parachuteBG.position.set(fromX, fromY);
            this._parachuteBG.visible = true;
        }, 0);
        tl.to(this._parachuteBG, {
            duration: duration,
            x: toX,
            y: MathHelper.randomNumberInRange(220, 260)
        });
        tl.to(this._parachuteBG.scale, {
            duration: duration,
            x: 0.75 * randomIdx,
            y: 0.75
        }, 0);
        tl.add(() => {
            this._parachuteBG.visible = false;

            this._canPlayParachuteBG = (!NolimitApplication.isLandscape && WalkOfShame.model.parsedGameData && WalkOfShame.model.parsedGameData.mode.indexOf(WalkOfShameGameMode.SUPER_FREESPIN) > -1);

            this.playParachuteBG(MathHelper.randomNumberInRange(3, 5));
        });

        this._parachuteBgTl = tl;
    }

    private playParachute(canPlaySound: boolean): void {
        if (!this._canPlayParachute) {
            return;
        }

        this.killTl(this._parachuteTl);

        const tl: TimelineLite = new TimelineLite(),
            randomIdx: number = ArrayHelper.getRandomValue([-1, 1]),
            screenBounds: ScreenBounds = NolimitApplication.screenBounds,
            center: number = screenBounds.center,
            duration: number = MathHelper.randomNumberInRange(5, 8),
            fromY: number = MathHelper.randomNumberInRange(screenBounds.top - 300, screenBounds.top - 100);
        let fromX: number = MathHelper.randomNumberInRange(screenBounds.left - 100, center),
            toX: number = MathHelper.randomNumberInRange(screenBounds.right + 100, screenBounds.right + 200),
            soundName: string = "";

        if (randomIdx === -1) {
            fromX = MathHelper.randomNumberInRange(screenBounds.right + 100, center);
            toX = MathHelper.randomNumberInRange(screenBounds.left - 100, screenBounds.left - 200);
        }

        if (this._parachuteBG === this._parachuteP1) {
            this._parachute = this._parachuteP2;

            if (canPlaySound) {
                soundName = WalkOfShameSound.PARACHUTE_SK.next();
            }
        } else {
            this._parachute = this._parachuteP1;

            if (canPlaySound) {
                soundName = WalkOfShameSound.PARACHUTE_PT.next();
            }
        }
        this._parachute.scale.x = randomIdx;

        tl.add(() => {
            this.addChild(this._parachute)
            this._parachute.scale.y = 1;
            this._parachute.position.set(fromX, fromY);
            this._parachute.visible = true;

            WalkOfShameSound.soundPlayer.playTrack({
                name: WalkOfShameSound.PARACHUTE
            });

            if (canPlaySound) {
                WalkOfShameSound.soundPlayer.playTrack({
                    name: soundName
                });
            }
        });
        tl.to(this._parachute, {
            duration: duration,
            x: toX,
            y: 350
        });
        tl.add(() => {
            this._parachute.visible = false;

            this._canPlayParachute = (!NolimitApplication.isLandscape && WalkOfShame.model.parsedGameData && WalkOfShame.model.parsedGameData.mode === WalkOfShameGameMode.SUPER_FREESPIN_RESPIN);

            this.playParachute((this._parachuteSoundCtr++ % 4 === 0));
        });

        this._parachuteTl = tl;
    }

    private killTl(tl: TimelineLite | null): void {
        if (tl) {
            tl.pause();
            tl.kill();
        }

        tl = null;
    }

    // #139
    private playLogoAnimForReSpin(): void {
        this._logoMainAnimation.skeleton.setSkinByName("Blue");
        this._logoMainAnimation.visible = true;
        this._logoMainAnimation.updateTransform();
        this.playMainLogoAnimation("flicker");
        this._mainLogoTl = new TimelineLite({
            repeat: -1
        });
        this._mainLogoTl.add(() => {
            this.playMainLogoAnimation("flicker");
        }, MathHelper.randomNumberInRange(2, 4));
    }

    private playMainLogoAnimation(name: string): void {
        this._logoMainAnimation.state.setEmptyAnimation(0, 0);
        this._logoMainAnimation.state.setAnimation(0, name, false);
    }
}